import React, {useState} from 'react'
import {View} from 'react-big-calendar'
import moment from 'moment'
import dayjs from 'dayjs'
import 'dayjs/locale/it'
import {Box, Popover, Stack, SxProps, Theme, ToggleButton, ToggleButtonGroup, Typography, useTheme} from '@mui/material'
import {DateCalendar} from '@mui/x-date-pickers/DateCalendar'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {PickerSelectionState} from '@mui/x-date-pickers/internals'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import AddIcon from '@mui/icons-material/Add'
import Button from 'src/components-new/primitives/Button'
import {CONTROL_HEIGHT, NEUTRAL_SCALE} from 'src/theme-new/tokens'

type CalendarToolbarNewProps = {
  date: Date
  view: View
  handleDateChange: (date: Date) => void
  handleViewChange: (view: View) => void
  // Optional — read-only calendars (e.g. PatientActivitiesCalendarNew) reuse this toolbar
  // without a "Nuovo evento" action. The button only renders when a handler is passed.
  onNewEvent?: () => void
  isMobile?: boolean
}

const CalendarToolbarNew: React.FC<CalendarToolbarNewProps> = ({
  date,
  view,
  handleDateChange,
  handleViewChange,
  onNewEvent,
  isMobile = false,
}) => {
  const theme = useTheme()

  // -------------------------------------------------------------------------
  // Month/year picker — lets the user jump directly to a month instead of
  // only stepping through it with the prev/next arrows.
  // -------------------------------------------------------------------------

  const [monthPickerAnchor, setMonthPickerAnchor] = useState<HTMLElement | null>(null)

  const openMonthPicker = (event: React.MouseEvent<HTMLElement>) => setMonthPickerAnchor(event.currentTarget)
  const closeMonthPicker = () => setMonthPickerAnchor(null)

  const handleMonthPickerChange = (value: dayjs.Dayjs | null, selectionState?: PickerSelectionState) => {
    if (value) handleDateChange(value.toDate())
    if (selectionState === 'finish') closeMonthPicker()
  }

  // -------------------------------------------------------------------------
  // Date math helpers — no moment dependency to avoid closure bloat
  // -------------------------------------------------------------------------

  const addMonths = (currentDate: Date, months: number) => {
    const d = new Date(currentDate)
    const day = d.getDate()
    d.setMonth(d.getMonth() + months)
    if (d.getDate() !== day) d.setDate(0)
    return d
  }

  const addWeeks = (currentDate: Date, weeks: number) => {
    const d = new Date(currentDate)
    d.setDate(d.getDate() + 7 * weeks)
    return d
  }

  const addDays = (currentDate: Date, days: number) => {
    const d = new Date(currentDate)
    d.setDate(d.getDate() + days)
    return d
  }

  // -------------------------------------------------------------------------
  // Navigation handlers
  // -------------------------------------------------------------------------

  const goToDayView = () => handleViewChange('day')
  const goToWeekView = () => handleViewChange('week')
  const goToMonthView = () => handleViewChange('month')

  const goToAgendaView = () => {
    handleDateChange(moment(date).startOf('month').toDate())
    handleViewChange('agenda')
  }

  const goToToday = () => {
    if (view === 'agenda') {
      handleDateChange(moment(new Date()).startOf('month').toDate())
    } else {
      handleDateChange(new Date())
    }
  }

  const goToBack = () => {
    if (view === 'week') handleDateChange(addWeeks(date, -1))
    else if (view === 'day') handleDateChange(addDays(date, -1))
    else handleDateChange(addMonths(date, -1))
  }

  const goToNext = () => {
    if (view === 'week') handleDateChange(addWeeks(date, +1))
    else if (view === 'day') handleDateChange(addDays(date, +1))
    else handleDateChange(addMonths(date, +1))
  }

  const handleViewToggle = (_: React.MouseEvent, newView: string | null) => {
    if (!newView) return
    if (newView === 'day') goToDayView()
    else if (newView === 'week') goToWeekView()
    else if (newView === 'month') goToMonthView()
    else if (newView === 'agenda') goToAgendaView()
  }

  const viewSelector = (
    <ToggleButtonGroup
      value={view}
      exclusive
      size="small"
      onChange={handleViewToggle}
      sx={{
        '& .MuiToggleButton-root': {
          height: 32,
          px: 1.5,
          fontSize: 13,
          fontWeight: 500,
          textTransform: 'none',
          borderColor: NEUTRAL_SCALE[300],
          color: NEUTRAL_SCALE[900],
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            borderColor: theme.palette.primary.main,
            '&:hover': {backgroundColor: theme.palette.primary.dark},
          },
          '&:not(.Mui-selected):hover': {backgroundColor: NEUTRAL_SCALE[50]},
        },
      }}
    >
      <ToggleButton value="day">Giorno</ToggleButton>
      <ToggleButton value="week">Settimana</ToggleButton>
      <ToggleButton value="month">Mese</ToggleButton>
      <ToggleButton value="agenda">Lista</ToggleButton>
    </ToggleButtonGroup>
  )

  // -------------------------------------------------------------------------
  // Date label
  // -------------------------------------------------------------------------

  const dateLabel = (() => {
    if (view === 'month' || view === 'agenda') return moment(date).format('MMMM YYYY')
    if (view === 'week') {
      return `${moment(date).startOf('week').format('DD')} - ${moment(date).endOf('week').format('DD MMM YYYY')}`
    }
    return moment(date).format('DD MMMM YYYY')
  })()

  // Clickable date label (button) that opens the month/year picker popover —
  // shared between the mobile and desktop layouts, only the outer sizing
  // (flex vs fixed minWidth) differs between them.
  const renderDateLabelButton = (containerSx: SxProps<Theme> = {}) => (
    <Box
      component="button"
      type="button"
      onClick={openMonthPicker}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.25,
        height: CONTROL_HEIGHT,
        cursor: 'pointer',
        border: 'none',
        background: 'none',
        font: 'inherit',
        borderRadius: '8px',
        px: 0.5,
        transition: 'background-color 120ms ease',
        '&:hover': {backgroundColor: NEUTRAL_SCALE[50]},
        ...containerSx,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: isMobile ? 15 : undefined,
          textTransform: 'capitalize',
        }}
      >
        {dateLabel}
      </Typography>
      <ArrowDropDownIcon fontSize="small" sx={{color: NEUTRAL_SCALE[500]}} />
    </Box>
  )

  const monthPickerPopover = (
    <Popover
      open={Boolean(monthPickerAnchor)}
      anchorEl={monthPickerAnchor}
      onClose={closeMonthPicker}
      anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      transformOrigin={{vertical: 'top', horizontal: 'center'}}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
        <DateCalendar
          value={dayjs(date)}
          views={['year', 'month']}
          openTo="month"
          onChange={handleMonthPickerChange}
          sx={{
            '& .MuiPickersMonth-monthButton, & .MuiPickersYear-yearButton': {
              transition: 'background-color 120ms ease, color 120ms ease',
            },
          }}
        />
      </LocalizationProvider>
    </Popover>
  )

  // -------------------------------------------------------------------------
  // Mobile layout — three stacked rows
  // -------------------------------------------------------------------------

  if (isMobile) {
    return (
      <>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mb: 2}}>
          {/* Row 1: nav arrows + date label + Oggi */}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            <Button variant="secondary" onClick={goToBack} sx={{minWidth: 0, px: 1}}>
              <ChevronLeftIcon />
            </Button>
            {renderDateLabelButton({flex: 1})}
            <Button variant="secondary" onClick={goToNext} sx={{minWidth: 0, px: 1}}>
              <ChevronRightIcon />
            </Button>
            <Button variant="secondary" onClick={goToToday} sx={{ml: 0.5, flexShrink: 0}}>
              Oggi
            </Button>
          </Box>

          {/* Row 2: view selector — compact segmented control */}
          {viewSelector}

          {/* Row 3: new event — full width, thumb-friendly (only when supported) */}
          {onNewEvent && (
            <Button variant="primary" startIcon={<AddIcon />} onClick={onNewEvent} sx={{width: '100%'}}>
              Nuovo evento
            </Button>
          )}
        </Box>
        {monthPickerPopover}
      </>
    )
  }

  // -------------------------------------------------------------------------
  // Desktop layout — three groups (nav | views | action) space-between
  // -------------------------------------------------------------------------

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 1,
        }}
      >
        {/* Left: navigation cluster — arrows, date label, Oggi */}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button variant="secondary" onClick={goToBack} sx={{minWidth: 0, px: 1}}>
            <ChevronLeftIcon />
          </Button>
          {renderDateLabelButton({minWidth: 180})}
          <Button variant="secondary" onClick={goToNext} sx={{minWidth: 0, px: 1}}>
            <ChevronRightIcon />
          </Button>
          <Button variant="secondary" onClick={goToToday} sx={{ml: 0.5}}>
            Oggi
          </Button>
        </Stack>

        {/* Center: view selector — compact segmented control */}
        {viewSelector}

        {/* Right: create new event (only when supported — otherwise an empty spacer keeps
            the view selector centered against the left navigation cluster) */}
        {onNewEvent ? (
          <Button variant="primary" startIcon={<AddIcon />} onClick={onNewEvent}>
            Nuovo evento
          </Button>
        ) : <Box />}
      </Box>
      {monthPickerPopover}
    </>
  )
}

export default CalendarToolbarNew
